import { useEffect, useState } from 'react';
import guestService from 'src/services/api/guest';

/**
 * Shared guest lookup state for check-in flows.
 *
 * @param {object} options
 * @param {string} options.eventURL - API path segment for getGuest
 * @param {() => object} [options.getRequestBody] - extra body fields (e.g. eventSlugname)
 * @param {string} [options.initialId] - optional id from query string to auto-load
 */
const useCheckInGuest = ({ eventURL, getRequestBody, initialId } = {}) => {
  const [guestID, setGuestID] = useState(initialId || '');
  const [Attendant, setAttendant] = useState({});
  const [loadedData, setLoadedData] = useState(false);
  const [errorData, setErrorData] = useState(null);

  useEffect(() => {
    if (errorData === null) return;
    const timer = setTimeout(() => setErrorData(null), 1800);
    return () => clearTimeout(timer);
  }, [errorData]);

  const getAttendantInfo = async (id) => {
    if (!eventURL) {
      setErrorData('Ruta de check-in no válida');
      setLoadedData(false);
      return;
    }

    let body = {};
    try {
      body = typeof getRequestBody === 'function' ? (await getRequestBody()) || {} : {};
    } catch (error) {
      setErrorData(error?.message || 'Disculpa, hubo un error, por favor intenta de nuevo.');
      setLoadedData(false);
      return;
    }

    guestService
      .getGuest(id, eventURL, body)
      .then((response) => {
        setLoadedData(false);
        if (response.data.success) {
          setGuestID('');
          setAttendant(response.data.Attendant);
        } else {
          setErrorData('No se encontró el usuario');
        }
      })
      .catch((error) => {
        if (error.response) setErrorData(error.response.data.message);
        setLoadedData(false);
      });
  };

  useEffect(() => {
    if (!initialId) return;
    setLoadedData(true);
    setGuestID(initialId);
    getAttendantInfo(initialId);
    // Load once from deep-link id on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAttendant = (e) => {
    e.preventDefault();
    if (!guestID) {
      setErrorData('No puede estar vacío');
      return;
    }
    setLoadedData(true);
    getAttendantInfo(guestID);
  };

  const resetForm = () => {
    setErrorData(null);
    setAttendant({});
    setGuestID('');
  };

  const handleInputs = (event) => {
    const { name, value } = event.target;
    setAttendant((prev) => ({ ...prev, [name]: value }));
  };

  const handleDropDown = ({ name, value }) => {
    setAttendant((prev) => ({ ...prev, [name]: value }));
  };

  return {
    guestID,
    setGuestID,
    Attendant,
    setAttendant,
    loadedData,
    errorData,
    setErrorData,
    getAttendant,
    getAttendantInfo,
    resetForm,
    handleInputs,
    handleDropDown,
  };
};

export default useCheckInGuest;
